import { canManageOrganization } from 'util/organizationAccess';
import {
  faSearch,
  faShieldAlt,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import corner6 from 'assets/img/illustrations/corner-6.png';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import SoftBadge from 'components/common/SoftBadge';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import {
  buildRepositoryNotaryJournalColumns,
  RepositoryNotaryJournalsTableContent,
  useRepositoryNotaryJournals
} from 'components/pages/admin/components/RepositoryNotaryJournalsList';
import SimpleBarReact from 'simplebar-react';
import NotaryJournalTableHeader from 'components/pages/notary/journal/NotaryJournalTableHeader';
import React, { useMemo } from 'react';
import { formatBillingDate } from 'util/organizationBillingFormat';
import { Alert, Dropdown, Form, InputGroup } from 'react-bootstrap';
import Avatar from 'components/common/Avatar';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { showViewEntryDialog } from 'stores/modal';
import Colors from 'util/Colors';
import { useListRepositoryUsersQuery } from 'rtk/org-admin/notaries.slice';
import { useGetOrganizationBillingProfileQuery } from 'rtk/org-admin/organizationBilling.slice';
import { isOrganizationBillingSetupError } from './components/OrganizationBillingSetupNotice';
import OrganizationBillingLockedJournals from './components/OrganizationBillingLockedJournals';

function getUserOptionName(user) {
  return user?.FullName?.trim() || user?.Email?.trim() || 'Unknown user';
}

const AdminNotaryUserJournals = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userGuid } = useParams();
  const { userMaster } = useSelector(state => state.app);
  const canManage = useSelector(state =>
    canManageOrganization(state.authentication?.userRoles)
  );
  const profileQuery = useGetOrganizationBillingProfileQuery(undefined, {
    skip: !canManage
  });
  const profile = profileQuery.data?.Data ?? profileQuery.data;
  const restricted = (profile?.Status ?? profile?.status) === 'Restricted';

  const {
    data: usersRes,
    isFetching: usersFetching,
    isLoading: usersLoading,
    error: usersError
  } = useListRepositoryUsersQuery({
    maxResults: 1000
  });

  const repositoryUsers = usersRes?.Objects || [];
  const selectedRepositoryUser = repositoryUsers.find(
    user => user?.UserGuid === userGuid
  );
  const usersLoadingState = usersFetching || usersLoading;

  const journals = useRepositoryNotaryJournals({ userGuid });
  const billingSetupRequired =
    profile?.BillingSetupRequired ||
    isOrganizationBillingSetupError(usersError) ||
    isOrganizationBillingSetupError(journals.queryError);
  const {
    searchString,
    setSearchString,
    pageSize,
    displayEntries,
    loading,
    refetch,
    onSearchChange,
    filterMonth,
    setFilterMonth,
    onCustomDateRange,
    customDateRange,
    setCustomDateRange
  } = journals;

  const onRowClicked = entry => {
    if (!entry?.GUID || billingSetupRequired || restricted) return;
    dispatch(
      showViewEntryDialog(
        entry.GUID,
        res => {
          if (res) refetch();
        },
        'admin-notary-user-journals'
      )
    );
  };

  const columns = useMemo(
    () =>
      buildRepositoryNotaryJournalColumns({
        onRowClicked,
        privacyEnabled: !!userMaster?.PrivacyEnabled,
        showNotary: !userGuid
      }),
    [userMaster?.PrivacyEnabled, userGuid]
  );

  const handleUserChange = selectedGuid => {
    if (selectedGuid === (userGuid || '')) return;
    navigate(
      selectedGuid
        ? `/admin/notaries/${selectedGuid}/entries`
        : '/admin/entries'
    );
  };

  if (billingSetupRequired && !restricted) {
    return <OrganizationBillingLockedJournals canManage={canManage} />;
  }

  if (restricted) {
    return (
      <Alert variant="warning">
        Hosted journal data is unavailable while organization billing is
        restricted. Use Billing to update payment details and restore access.
      </Alert>
    );
  }

  return (
    <AdvanceTableWrapper
      columns={columns}
      data={displayEntries}
      selection={false}
      sortable
      pagination
      perPage={pageSize}
      sortBy={[]}
    >
      <FalconComponentCard noGuttersBottom>
        {selectedRepositoryUser?.HostedDataState === 'DeletionPending' && (
          <Alert variant="warning" className="m-3 mb-0">
            Hosted-data deletion is pending. Journal access ends{' '}
            <strong>
              {formatBillingDate(
                selectedRepositoryUser.HostedDataDeletionEffectiveUtc,
                'lll'
              )}
            </strong>
            .
          </Alert>
        )}
        <FalconComponentCard.Header
          image={corner6}
          className="px-4"
          title="Journal entries"
          subtitle={
            userGuid
              ? 'Browse notary journal entries for this user'
              : 'Browse journal entries shared by all connected notaries'
          }
          endEl={
            <Flex direction="column" alignItems="end" className="gap-2">
              <Flex
                alignItems="center"
                justifyContent="end"
                className="gap-2 flex-wrap"
              >
                <InputGroup size="sm" style={{ width: 300 }}>
                  <InputGroup.Text className="bg-white">
                    <FontAwesomeIcon icon={faSearch} className="text-500" />
                  </InputGroup.Text>
                  <Form.Control
                    type="search"
                    placeholder="Search notary or signer"
                    aria-label="Search journal entries by notary or signer"
                    value={searchString || ''}
                    onChange={e => {
                      setSearchString(e.target.value);
                      onSearchChange();
                    }}
                  />
                </InputGroup>
                <Dropdown align="end" style={{ width: 280 }}>
                  <Dropdown.Toggle
                    variant="falcon-default"
                    size="sm"
                    className="w-100 d-flex align-items-center justify-content-between text-start"
                    disabled={usersLoadingState}
                    aria-label="Filter by notary"
                  >
                    <span className="text-truncate me-2">
                      {usersLoadingState
                        ? 'Loading notaries…'
                        : selectedRepositoryUser
                        ? getUserOptionName(selectedRepositoryUser)
                        : 'All notaries'}
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    className="py-1"
                    style={{ minWidth: 320, maxHeight: 320, overflowY: 'auto' }}
                  >
                    <Dropdown.Item
                      active={!userGuid}
                      onClick={() => handleUserChange('')}
                      className="d-flex align-items-center py-2"
                    >
                      <Avatar
                        size="xl"
                        icon={faUsers}
                        className="me-2"
                        mediaClass="bg-soft-primary text-primary"
                      />
                      <div className="lh-sm">
                        <div className="fw-semi-bold fs--1">All notaries</div>
                        <div className="fs--2 opacity-75">
                          Entries from every connected notary
                        </div>
                      </div>
                    </Dropdown.Item>
                    <Dropdown.Divider className="my-1" />
                    {repositoryUsers.map(user => {
                      const guid = user?.UserGuid;
                      if (!guid) return null;
                      const name = getUserOptionName(user);
                      const email = user?.Email?.trim();
                      return (
                        <Dropdown.Item
                          key={guid}
                          active={guid === userGuid}
                          onClick={() => handleUserChange(guid)}
                          className="d-flex align-items-center py-2"
                        >
                          <Avatar
                            size="xl"
                            name={name}
                            className="me-2"
                            mediaClass="bg-soft-primary text-primary"
                          />
                          <div className="lh-sm text-truncate">
                            <div className="fw-semi-bold fs--1 text-truncate">
                              {name}
                            </div>
                            {email && email !== name ? (
                              <div className="fs--2 opacity-75 text-truncate">
                                {email}
                              </div>
                            ) : null}
                          </div>
                        </Dropdown.Item>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              </Flex>
              {userMaster?.PrivacyEnabled && (
                <SoftBadge pill bg="success" className="me-2">
                  <FontAwesomeIcon
                    size="sm"
                    color={Colors.success.color}
                    icon={faShieldAlt}
                    className="me-0"
                  />{' '}
                  Privacy mode
                </SoftBadge>
              )}
              <NotaryJournalTableHeader
                table
                searchString={searchString}
                setSearchString={setSearchString}
                onSearchChange={onSearchChange}
                selectedRowIds={{}}
                filterMonth={filterMonth}
                setFilterMonth={setFilterMonth}
                onCustomDateRange={onCustomDateRange}
                customDateRange={customDateRange}
                setCustomDateRange={setCustomDateRange}
              />
            </Flex>
          }
        />
        <div className="py-0 px-4">
          <RepositoryNotaryJournalsTableContent
            journals={journals}
            showEmptyState
            pageSizeOptions={[5, 10, 15, 25]}
          />
          {displayEntries.length > 0 ? (
            <SimpleBarReact>
              <AdvanceTable
                table
                headerClassName="bg-200 text-900 text-nowrap align-middle"
                rowClassName="align-middle white-space-nowrap"
                tableProps={{
                  size: 'sm',
                  striped: true,
                  className: `fs--1 mb-0 overflow-hidden${
                    loading ? ' refreshing-skeleton' : ''
                  }`
                }}
              />
            </SimpleBarReact>
          ) : null}
        </div>
      </FalconComponentCard>
    </AdvanceTableWrapper>
  );
};

export default AdminNotaryUserJournals;
